import React, { useState, useEffect, useRef } from 'react';
import { FaPencilRuler, FaHardHat, FaBuilding, FaPlus, FaArrowUp, FaArrowDown, FaTimes, FaTrash, FaCalculator, FaDownload, FaTable, FaBullseye } from 'react-icons/fa';
import { ArrowLeft, Search, Eye, Sparkles, FileText, X, Download } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getBytes } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import SaveButton from '../components/SaveButton';
import EditButton from '../components/EditButton';
import { createKPIParser } from '../utils/formulaParser';
import { fetchGroupEmojis, defaultGroupEmojis } from '../services/groupEmojiService';
import { fetchKpiHtmlTemplate, renderKpiHtmlTemplate, DEFAULT_KPI_HTML_TEMPLATE } from '../services/kpiHtmlTemplateService';
import { downloadTailored } from '../utils/tailorDownloads';
import PortalBackground from '../components/PortalBackground';

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client's KPIs instead of their own — the account's KPI jobs
// come from overrideCompanyId and customizations are keyed by overrideUserId.
const PortalKPIs = ({ embedded = false, overrideCompanyId = null, overrideUserId = null }) => {
  const { currentUser } = useAuth();
  // The uid whose customizations this page reads/writes.
  const targetUid = overrideUserId || currentUser?.uid || null;
  const { darkMode } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  const openedParamRef = useRef(null);
  const [activeTab, setActiveTab] = useState('design');
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Template and customization state
  const [kpiTemplates, setKpiTemplates] = useState([]);
  const [userCustomizations, setUserCustomizations] = useState({});
  const [companyGoals, setCompanyGoals] = useState([]);
  const [departmentKPIs, setDepartmentKPIs] = useState({
    design: [],
    construction: [],
    office: []
  });

  // Formula parser state
  const [formulaParser, setFormulaParser] = useState(null);
  
  // Formula-related state for role KPIs
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [selectedKpiForFormula, setSelectedKpiForFormula] = useState(null);
  const [formulaError, setFormulaError] = useState('');
  const [formulaBuilder, setFormulaBuilder] = useState([]);
  const [numberInput, setNumberInput] = useState('');
  const [availableOperations] = useState([
    { value: '+', label: 'Add (+)', symbol: '+' },
    { value: '-', label: 'Subtract (-)', symbol: '-' },
    { value: '*', label: 'Multiply (×)', symbol: '*' },
    { value: '/', label: 'Divide (÷)', symbol: '/' },
    { value: '(', label: 'Open Parenthesis', symbol: '(' },
    { value: ')', label: 'Close Parenthesis', symbol: ')' },
  ]);

  const tabs = [
    { id: 'design', label: 'Design', icon: <FaPencilRuler /> },
    { id: 'construction', label: 'Construction', icon: <FaHardHat /> },
    { id: 'office', label: 'Office', icon: <FaBuilding /> }
  ];

  // Department KPIs are shown as grouped sections (like the portal SOPs / Job
  // Descriptions pages) with a search bar and group filter instead of tabs.
  const KPI_SECTIONS = [
    { id: 'design', label: 'Design', emoji: '📐' },
    { id: 'construction', label: 'Construction', emoji: '👷' },
    { id: 'office', label: 'Office', emoji: '🏢' },
  ];
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  // KPI Tracking uses its own search + group filter over the same sections
  // (plus a Company section) rather than spreadsheet tabs.
  const [trackingSearch, setTrackingSearch] = useState('');
  const [trackingGroupFilter, setTrackingGroupFilter] = useState('all');
  const [groupEmojis, setGroupEmojis] = useState(defaultGroupEmojis('kpi'));

  useEffect(() => {
    fetchGroupEmojis('kpi').then(setGroupEmojis).catch(() => {});
  }, []);

  const TRACKING_HEADERS = [
    'Who',
    'Description',
    'Current Month On / Off Track',
    'Q1',
    'Q2',
    'Q3',
    'Q4',
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const COMPANY_TRACKING_HEADERS = ['Q1', 'Q2', 'Q3', 'Q4', 'Total'];

  const COMPANY_TRACKING_ROW_DEFS = [
    { key: 'build', rowTitle: 'Build Contracts', sourceLabels: ['Build Revenue', 'Build Contracts'] },
    { key: 'design', rowTitle: 'Design Contracts', sourceLabels: ['Design Revenue', 'Design Contracts'] },
    { key: 'gp', rowTitle: 'GP', sourceLabels: ['Gross Profit', 'GP'] }
  ];

  const trackingSheetTabs = [
    { id: 'company', label: 'Company', icon: <FaBullseye /> },
    { id: 'design', label: 'Design', icon: <FaPencilRuler /> },
    { id: 'construction', label: 'Construction', icon: <FaHardHat /> },
    { id: 'office', label: 'Office', icon: <FaBuilding /> }
  ];

  // Fetch KPI templates and user customizations
  useEffect(() => {
    if (currentUser) {
      loadKPITemplatesAndCustomizations();
    }
  }, [currentUser, targetUid, overrideCompanyId]);

  // Update formula parser when company goals change or user customizations change
  useEffect(() => {
    // Get user's company KPI values for formula parsing
    const templateId = kpiTemplates[0]?.id;
    const userCustomization = userCustomizations[templateId];
    const userCompanyGoals = userCustomization?.companyGoals || [];
    
    // Create a mapping of user's company KPI values for formula parsing
    const userCompanyValues = {};
    userCompanyGoals.forEach(goal => {
      if (goal.value !== 'Set Value') {
        userCompanyValues[goal.label] = goal.value;
      }
    });
    
    // Use user's values if available, otherwise use template structure
    const formulaGoals = companyGoals.map(goal => ({
      ...goal,
      value: userCompanyValues[goal.label] || goal.value
    }));
    
    const parser = createKPIParser(formulaGoals);
    setFormulaParser(parser);
  }, [companyGoals, userCustomizations, kpiTemplates]);


  const loadKPITemplatesAndCustomizations = async () => {
    try {
      setIsLoading(true);
      
      // Get the target user's accountId from their user document
      let accountId = overrideCompanyId || null;
      if (!accountId && targetUid) {
        const userDoc = await getDoc(doc(db, 'users', targetUid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          accountId = userData.accountId;
        }
      }

      // Fetch KPI templates
      const templateQuerySnapshot = await getDocs(collection(db, 'kpiTemplates'));
      const templates = templateQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Fetch account-specific KPI jobs if accountId exists
      let accountKPIJobs = [];
      if (accountId) {
        try {
          const accountKPIJobsQuerySnapshot = await getDocs(collection(db, 'accounts', accountId, 'kpiJobs'));
          accountKPIJobs = accountKPIJobsQuerySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific KPI jobs:', error);
          // Continue with just template KPIs if account KPI jobs fail to load
        }
      }
      
      setKpiTemplates(templates);
      
      // Fetch user customizations
      const customizationsQuery = query(
        collection(db, 'userKPICustomizations'),
        where('userId', '==', targetUid)
      );
      const customizationsSnapshot = await getDocs(customizationsQuery);
      const customizations = {};
      
      customizationsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        customizations[data.templateId] = {
          id: doc.id,
          ...data
        };
      });
      
      setUserCustomizations(customizations);
      
      // Load the most recent template if available
      if (templates.length > 0) {
        const latestTemplate = templates[0];
        const templateId = latestTemplate.id;
        const userCustomization = customizations[templateId];
        
        // Use template structure but with placeholder values for company goals
        const placeholderCompanyGoals = (latestTemplate.companyGoals || []).map(goal => ({
          ...goal,
          value: 'Set Value'
        }));
        setCompanyGoals(placeholderCompanyGoals);
        
        // Apply user customizations to department KPIs if they exist
        let departmentKPIsData = latestTemplate.departmentKPIs || {
          design: [],
          construction: [],
          office: []
        };
        
        // Apply customizations to template jobs (like JobDescriptions does)
        if (userCustomization?.departmentKPIs) {
          const updatedJobs = { ...departmentKPIsData };
          Object.keys(updatedJobs).forEach(department => {
            updatedJobs[department] = updatedJobs[department].map((job, index) => {
              // Create a stable ID for the job based on department and index
              const jobId = `${department}_${index}`;
              const jobWithId = { ...job, jobId };
              
              const customizedJob = userCustomization.departmentKPIs[department]?.find(j => j.jobId === jobId);
              if (customizedJob) {
                return {
                  ...jobWithId,
                  ...customizedJob,
                  hasCustomizations: true
                };
              }
              return {
                ...jobWithId,
                hasCustomizations: false
              };
            });
          });
          departmentKPIsData = updatedJobs;
        } else {
          // Set hasCustomizations to false for all jobs if no customizations
          const updatedJobs = { ...departmentKPIsData };
          Object.keys(updatedJobs).forEach(department => {
            updatedJobs[department] = updatedJobs[department].map((job, index) => ({
              ...job,
              jobId: `${department}_${index}`,
              hasCustomizations: false
            }));
          });
          departmentKPIsData = updatedJobs;
        }
        
        // Add account-specific KPI jobs
        accountKPIJobs.forEach(kpiJob => {
          if (departmentKPIsData[kpiJob.department]) {
            departmentKPIsData[kpiJob.department].push({
              ...kpiJob,
              hasCustomizations: true,
              isAccountSpecific: true
            });
          }
        });

        setDepartmentKPIs(departmentKPIsData);
      }
    } catch (error) {
      console.error('Error loading KPI templates and customizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchKPITemplates = async () => {
    try {
      setIsLoading(true);
      const querySnapshot = await getDocs(collection(db, 'kpiTemplates'));
      const templates = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setKpiTemplates(templates);
      
      // Load the most recent template if available
      if (templates.length > 0) {
        const latestTemplate = templates[0];
        // Use template structure but with placeholder values for company goals
        const placeholderCompanyGoals = (latestTemplate.companyGoals || []).map(goal => ({
          ...goal,
          value: 'Set Value'
        }));
        setCompanyGoals(placeholderCompanyGoals);
        setDepartmentKPIs(latestTemplate.departmentKPIs || {
          design: [],
          construction: [],
          office: []
        });
      }
    } catch (error) {
      console.error('Error fetching KPI templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserCustomizations = async () => {
    try {
      const customizationsQuery = query(
        collection(db, 'userKPICustomizations'),
        where('userId', '==', targetUid)
      );
      const querySnapshot = await getDocs(customizationsQuery);
      const customizations = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        customizations[data.templateId] = {
          id: doc.id,
          ...data
        };
      });
      
      setUserCustomizations(customizations);
    } catch (error) {
      console.error('Error fetching user customizations:', error);
    }
  };

  // Calculate KPI value using formula if present
  const calculateKpiValue = (kpi) => {
    if (!kpi.formula || !formulaParser) {
      return kpi.value;
    }
    
    // Check if formula references company KPIs that haven't been set yet
    const templateId = kpiTemplates[0]?.id;
    const userCustomization = userCustomizations[templateId];
    const userCompanyGoals = userCustomization?.companyGoals || [];
    
    // Check if any referenced company KPIs are still "Set Value"
    const hasUnsetCompanyKPIs = userCompanyGoals.some(goal => 
      goal.value === 'Set Value' && kpi.formula.includes(goal.label)
    );
    
    if (hasUnsetCompanyKPIs) {
      return 'Set Company KPIs First';
    }
    
    const result = formulaParser.evaluate(kpi.formula);
    if (result !== null) {
      return formulaParser.formatResult(result, kpi.formula);
    }
    return kpi.value;
  };

  // Get display data for a KPI (user customizations only)
  const getDisplayKPI = (kpi, templateId) => {
    const customization = userCustomizations[templateId];
    if (customization) {
      // Find the customized version of this KPI
      const customizedKPI = customization.companyGoals?.find(goal => goal.label === kpi.label);
      
      if (customizedKPI) {
        return {
          ...kpi,
          ...customizedKPI,
          value: customizedKPI.formula ? calculateKpiValue(customizedKPI) : customizedKPI.value,
          hasCustomizations: true
        };
      }
    }
    
    // Return placeholder for user input
    return {
      ...kpi,
      value: 'Set Value',
      hasCustomizations: false
    };
  };

  // Get display data for department KPIs (data is already customized in state)
  const getDisplayDepartmentKPIs = () => {
    return {
      design: departmentKPIs.design.map(job => ({
        ...job,
        kpis: job.kpis?.map(kpi => ({
          ...kpi,
          value: kpi.formula ? calculateKpiValue(kpi) : kpi.value
        })) || job.kpis
      })),
      construction: departmentKPIs.construction.map(job => ({
        ...job,
        kpis: job.kpis?.map(kpi => ({
          ...kpi,
          value: kpi.formula ? calculateKpiValue(kpi) : kpi.value
        })) || job.kpis
      })),
      office: departmentKPIs.office.map(job => ({
        ...job,
        kpis: job.kpis?.map(kpi => ({
          ...kpi,
          value: kpi.formula ? calculateKpiValue(kpi) : kpi.value
        })) || job.kpis
      }))
    };
  };

  const handleKpiClick = (kpi) => {
    setSelectedKPI(kpi);
    setEditedValue(kpi.value);
    setIsModalOpen(true);
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setEditedJob({ ...job });
    setIsJobModalOpen(true);
  };

  // View opens the modal read-only; Tailor drops straight into edit mode
  // (mirrors the portal Job Descriptions View / Tailor cards).
  const handleJobView = (job) => {
    handleJobClick(job);
    setIsEditing(false);
  };

  const handleJobTailor = (job) => {
    handleJobClick(job);
    setIsEditing(true);
  };

  // Sidebar search deep link: /portal/kpis?open=<role title> opens that role.
  // Guarded by a ref so closing the modal doesn't immediately reopen it.
  useEffect(() => {
    if (!openParam || openedParamRef.current === openParam) return;
    const display = getDisplayDepartmentKPIs();
    const wanted = openParam.toLowerCase();
    for (const department of Object.keys(display)) {
      const match = (display[department] || []).find(
        (job) => (job.title || '').toLowerCase() === wanted
      );
      if (match) {
        openedParamRef.current = openParam;
        setActiveTab(department);
        handleJobView(match);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam, departmentKPIs]);

  const handleSave = async () => {
    if (selectedKPI) {
      try {
        const templateId = kpiTemplates[0]?.id;
        
        // Get existing customizations or start fresh
        const existingCustomization = userCustomizations[templateId];
        let existingCompanyGoals = existingCustomization?.companyGoals || [];
        
        // Update only the specific KPI that was edited
        const updatedCompanyGoals = existingCompanyGoals.map(goal => 
          goal.label === selectedKPI.label 
            ? { ...goal, value: editedValue }
            : goal
        );
        
        // If this KPI doesn't exist in customizations yet, add it
        if (!existingCompanyGoals.find(goal => goal.label === selectedKPI.label)) {
          const templateGoal = companyGoals.find(goal => goal.label === selectedKPI.label);
          updatedCompanyGoals.push({
            ...templateGoal,
            value: editedValue
          });
        }
        
        // Save user customization
        const customizationData = {
          userId: targetUid,
          templateId: templateId,
          companyGoals: updatedCompanyGoals,
          updatedAt: serverTimestamp()
        };
        
        if (existingCustomization) {
          // Update existing customization
          await updateDoc(doc(db, 'userKPICustomizations', existingCustomization.id), customizationData);
        } else {
          // Create new customization
          await addDoc(collection(db, 'userKPICustomizations'), {
            ...customizationData,
            createdAt: serverTimestamp()
          });
        }

        // Refresh customizations
        await fetchUserCustomizations();
        
        setIsModalOpen(false);
        setSelectedKPI(null);
      } catch (error) {
        console.error('Error saving KPI customization:', error);
      }
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedKPI(null);
    setEditedValue('');
  };

  const handleJobModalClose = () => {
    setIsJobModalOpen(false);
    setSelectedJob(null);
  };

  const handleIncrement = () => {
    if (selectedKPI) {
      let currentValue = parseFloat(editedValue.replace(/[^0-9.-]+/g, ''));
      // Treat "Set Value" as 0
      if (isNaN(currentValue)) {
        currentValue = 0;
      }
      
      if (selectedKPI.label === 'Gross Profit') {
        const newValue = `>${currentValue + 1}%`;
        setEditedValue(newValue);
      } else if (selectedKPI.label === 'Build Revenue' || selectedKPI.label === 'Design Revenue') {
        const newValue = `$${(currentValue + 0.5).toFixed(1)}M`;
        setEditedValue(newValue);
      }
    }
  };

  const handleDecrement = () => {
    if (selectedKPI) {
      let currentValue = parseFloat(editedValue.replace(/[^0-9.-]+/g, ''));
      // Treat "Set Value" as 0
      if (isNaN(currentValue)) {
        currentValue = 0;
      }
      
      if (selectedKPI.label === 'Gross Profit') {
        const newValue = `>${Math.max(0, currentValue - 1)}%`;
        setEditedValue(newValue);
      } else if (selectedKPI.label === 'Build Revenue' || selectedKPI.label === 'Design Revenue') {
        const newValue = `$${Math.max(0, currentValue - 0.5).toFixed(1)}M`;
        setEditedValue(newValue);
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleJobSave = async () => {
    if (isEditing && editedJob) {
      try {
        const templateId = kpiTemplates[0]?.id;
        
        // Get existing customizations or start fresh
        const existingCustomization = userCustomizations[templateId];
        let existingDepartmentKPIs = existingCustomization?.departmentKPIs || {
          design: [],
          construction: [],
          office: []
        };
        
        // Update only the specific department's job that was edited using stable jobId
        const updatedDepartmentKPIs = {
          ...existingDepartmentKPIs,
          [editedJob.department]: existingDepartmentKPIs[editedJob.department].map(job => 
            job.jobId === selectedJob.jobId ? editedJob : job
          )
        };
        
        // If this job doesn't exist in customizations yet, add it
        const existingJob = existingDepartmentKPIs[editedJob.department].find(job => job.jobId === selectedJob.jobId);
        if (!existingJob) {
          // Add the new job
          updatedDepartmentKPIs[editedJob.department] = [
            ...updatedDepartmentKPIs[editedJob.department],
            editedJob
          ];
        }
        
        // Save user customization for department KPIs
        const customizationData = {
          userId: targetUid,
          templateId: templateId,
          departmentKPIs: updatedDepartmentKPIs,
          updatedAt: serverTimestamp()
        };

        if (existingCustomization) {
          // Update existing customization
          await updateDoc(doc(db, 'userKPICustomizations', existingCustomization.id), customizationData);
        } else {
          // Create new customization
          const docRef = await addDoc(collection(db, 'userKPICustomizations'), {
            ...customizationData,
            createdAt: serverTimestamp()
          });
          
          // Update local customizations state
          setUserCustomizations(prev => ({
            ...prev,
            [templateId]: {
              id: docRef.id,
              ...customizationData
            }
          }));
        }

        // Update local state to show the customized version
        setSelectedJob({
          ...editedJob,
          hasCustomizations: true
        });
        
        // Update the department KPIs display to show customized version
        setDepartmentKPIs(prev => ({
          ...prev,
          [editedJob.department]: prev[editedJob.department].map(job => 
            job.jobId === selectedJob.jobId ? { ...editedJob, hasCustomizations: true } : job
          )
        }));
        
        // Update userCustomizations state to reflect the changes
        setUserCustomizations(prev => ({
          ...prev,
          [templateId]: {
            ...prev[templateId],
            departmentKPIs: updatedDepartmentKPIs
          }
        }));
        
        setIsJobModalOpen(false);
        setIsEditing(false);
        setSelectedJob(null);
        setEditedJob(null);
      } catch (error) {
        console.error('Error saving job customization:', error);
        alert('Error saving job customization. Please try again.');
      }
    }
  };

  const handleJobCancel = () => {
    setIsJobModalOpen(false);
    setIsEditing(false);
    setSelectedJob(null);
    setEditedJob(null);
  };

  const handleResetJobToTemplate = async () => {
    try {
      const templateId = kpiTemplates[0]?.id;
      const customization = userCustomizations[templateId];
      
      if (customization?.departmentKPIs) {
        // Remove this job from customizations
        const updatedDepartmentKPIs = {
          ...customization.departmentKPIs,
          [selectedJob.department]: customization.departmentKPIs[selectedJob.department].filter(
            job => job.jobId !== selectedJob.jobId
          )
        };
        
        // Update the customization in Firestore
        await updateDoc(doc(db, 'userKPICustomizations', customization.id), {
          departmentKPIs: updatedDepartmentKPIs,
          updatedAt: serverTimestamp()
        });
        
        // Refresh customizations
        await fetchUserCustomizations();
        
        // Reset the job to template version
        const templateJob = departmentKPIs[selectedJob.department].find(job => job.jobId === selectedJob.jobId);
        if (templateJob) {
          setSelectedJob(templateJob);
          setEditedJob(templateJob);
        }
      }
    } catch (error) {
      console.error('Error resetting job to template:', error);
      alert('Error resetting job to template. Please try again.');
    }
  };

  const handleJobTitleChange = (e) => {
    setEditedJob({ ...editedJob, title: e.target.value });
  };

  const handleKpiChange = (index, field, value) => {
    const updatedKpis = [...editedJob.kpis];
    updatedKpis[index] = { ...updatedKpis[index], [field]: value };
    setEditedJob({ ...editedJob, kpis: updatedKpis });
  };

  const handleAddKpi = () => {
    setEditedJob({
      ...editedJob,
      kpis: [
        ...editedJob.kpis,
        {
          title: 'New KPI',
          value: '0',
          period: 'per quarter'
        }
      ]
    });
  };

  const handleDeleteKpi = (index) => {
    setEditedJob({
      ...editedJob,
      kpis: editedJob.kpis.filter((_, i) => i !== index)
    });
  };

  const handleAddNew = () => {
    const newJob = {
      title: 'New Role',
      value: 'View Details',
      department: activeTab,
      kpis: [
        {
          title: 'New KPI',
          value: '0',
          period: 'per quarter'
        }
      ]
    };
    setSelectedJob(newJob);
    setEditedJob(newJob);
    setIsEditing(true);
    setIsJobModalOpen(true);
  };

  const handleDepartmentChange = (e) => {
    setEditedJob({ ...editedJob, department: e.target.value });
  };

  // Formula-related functions for role KPIs
  const handleFormulaClick = (kpi, index) => {
    setSelectedKpiForFormula({ kpi, index });
    setFormulaError('');
    setShowFormulaModal(true);
    
    // Parse existing formula into builder
    if (kpi.formula) {
      const parsed = parseFormulaToBuilder(kpi.formula);
      setFormulaBuilder(parsed);
    } else {
      setFormulaBuilder([]);
    }

    // Create role-specific formula parser if editing a role KPI
    if (editedJob && editedJob.title) {
      const roleParser = createRoleFormulaParser(editedJob.title);
      setFormulaParser(roleParser);
    }
  };

  const parseFormulaToBuilder = (formula) => {
    // Simple parser to convert formula string back to builder format
    const parts = formula.split(/\s+/);
    const builder = [];
    
    parts.forEach(part => {
      if (part.match(/^\{.*\}$/)) {
        // Variable
        const cleanPart = part.replace(/[{}]/g, '');
        const [category, key] = cleanPart.split('.');
        builder.push({ type: 'variable', category, key, value: part });
      } else if (['+', '-', '*', '/', '(', ')'].includes(part)) {
        // Operation
        builder.push({ type: 'operation', value: part });
      } else if (!isNaN(part)) {
        // Number
        builder.push({ type: 'number', value: part });
      }
    });
    
    return builder;
  };

  const handleFormulaSave = () => {
    const formulaString = buildFormulaString();
    
    if (!formulaParser) return;
    
    const validation = formulaParser.validateFormula(formulaString);
    if (!validation.isValid) {
      setFormulaError(validation.error);
      return;
    }
    
    // Update the KPI with the formula
    const updatedKpis = [...editedJob.kpis];
    updatedKpis[selectedKpiForFormula.index] = {
      ...updatedKpis[selectedKpiForFormula.index],
      formula: formulaString,
      value: calculateKpiValue({ ...updatedKpis[selectedKpiForFormula.index], formula: formulaString })
    };
    setEditedJob({ ...editedJob, kpis: updatedKpis });
    
    setShowFormulaModal(false);
    setSelectedKpiForFormula(null);
    setFormulaError('');
    setFormulaBuilder([]);
  };

  const handleFormulaModalClose = () => {
    setShowFormulaModal(false);
    setSelectedKpiForFormula(null);
    setFormulaError('');
    setFormulaBuilder([]);
    setNumberInput('');
    
    // Reset formula parser to default
    const templateId = kpiTemplates[0]?.id;
    const userCustomization = userCustomizations[templateId];
    const userCompanyGoals = userCustomization?.companyGoals || [];
    const userCompanyValues = {};
    userCompanyGoals.forEach(goal => {
      if (goal.value !== 'Set Value') {
        userCompanyValues[goal.label] = goal.value;
      }
    });
    const formulaGoals = companyGoals.map(goal => ({
      ...goal,
      value: userCompanyValues[goal.label] || goal.value
    }));
    const parser = createKPIParser(formulaGoals);
    setFormulaParser(parser);
  };

  // Formula builder functions
  const handleAddToFormula = (item) => {
    setFormulaBuilder([...formulaBuilder, item]);
  };

  const handleAddNumberToFormula = () => {
    if (numberInput && !isNaN(numberInput)) {
      setFormulaBuilder([...formulaBuilder, { type: 'number', value: numberInput }]);
      setNumberInput('');
    }
  };

  const handleClearFormula = () => {
    setFormulaBuilder([]);
  };

  const handleRemoveFromFormula = (index) => {
    const updated = formulaBuilder.filter((_, i) => i !== index);
    setFormulaBuilder(updated);
  };

  const buildFormulaString = () => {
    return formulaBuilder.map(item => {
      if (item.type === 'variable') {
        return `{${item.category}.${item.key}}`;
      } else if (item.type === 'operation') {
        return item.value;
      } else if (item.type === 'number') {
        return item.value;
      }
      return '';
    }).join(' ');
  };

  // Generate role-specific variables based on the current role being edited
  const getRoleSpecificVariables = (roleTitle) => {
    if (!roleTitle) return [];
    
    // Count the number of people in this role across all departments
    let count = 0;
    Object.values(departmentKPIs).forEach(department => {
      department.forEach(job => {
        if (job.title === roleTitle) {
          count++;
        }
      });
    });
    
    return [{
      display: `numberOf${roleTitle.replace(/\s+/g, '')}`,
      value: count.toString(),
      category: 'role'
    }];
  };

  // Create formula parser with role-specific variables for role KPIs
  const createRoleFormulaParser = (roleTitle) => {
    const roleVariables = getRoleSpecificVariables(roleTitle);
    
    // Create a custom variables array that includes role-specific variables
    const allVariables = roleVariables.map(v => ({
      id: `role_${v.display}`,
      name: v.display,
      value: v.value
    }));
    
    // Get user's company KPI values for formula parsing
    const templateId = kpiTemplates[0]?.id;
    const userCustomization = userCustomizations[templateId];
    const userCompanyGoals = userCustomization?.companyGoals || [];
    const userCompanyValues = {};
    userCompanyGoals.forEach(goal => {
      if (goal.value !== 'Set Value') {
        userCompanyValues[goal.label] = goal.value;
      }
    });
    const formulaGoals = companyGoals.map(goal => ({
      ...goal,
      value: userCompanyValues[goal.label] || goal.value
    }));
    
    return createKPIParser(formulaGoals, allVariables);
  };

  // Shared branding for KPI downloads: logo (as a data URL so it renders in
  // html2canvas / Word), company name, and brand colors. Mirrors the SOPs helper.
  const getKpiBranding = async () => {
    let companyLogo = null;
    let companyName = '';
    let primaryColor = '#dc2626';
    let secondaryColor = '#1f2937';
    if (targetUid) {
      try {
        const userDoc = await getDoc(doc(db, 'users', targetUid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.companyLogo) companyLogo = userData.companyLogo;
          if (userData.company) companyName = userData.company;
          if (userData.companyColors) {
            primaryColor = userData.companyColors.primary || primaryColor;
            secondaryColor = userData.companyColors.secondary || secondaryColor;
          }
        }
        if (!companyLogo && !overrideUserId) {
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            const userData = internalUserDoc.data();
            if (userData.companyLogo) companyLogo = userData.companyLogo;
            if (!companyName && userData.company) companyName = userData.company;
            if (userData.companyColors) {
              primaryColor = userData.companyColors.primary || primaryColor;
              secondaryColor = userData.companyColors.secondary || secondaryColor;
            }
          }
        }
      } catch (e) {
        console.error('Error fetching user settings:', e);
      }
    }
    const loadLogoAsDataUrl = async (url) => {
      try {
        const isFirebaseStorageURL =
          url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
        if (isFirebaseStorageURL && currentUser) {
          const urlMatch = url.match(/\/o\/([^?]+)/);
          if (!urlMatch) return null;
          const storagePath = decodeURIComponent(urlMatch[1]);
          const storageRef = ref(storage, storagePath);
          const bytes = await getBytes(storageRef);
          let blobType = 'image/png';
          if (storagePath.endsWith('.svg')) blobType = 'image/svg+xml';
          else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) blobType = 'image/jpeg';
          else if (storagePath.endsWith('.gif')) blobType = 'image/gif';
          else if (storagePath.endsWith('.webp')) blobType = 'image/webp';
          const blob = new Blob([bytes], { type: blobType });
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        }
        if (url.startsWith('http') || url.startsWith('/')) {
          const response = await fetch(url);
          if (!response.ok) return null;
          const blob = await response.blob();
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        }
        return null;
      } catch (e) {
        return null;
      }
    };
    let logoDataUrl = null;
    if (companyLogo) {
      const isFirebaseStorageURL =
        companyLogo.includes('firebasestorage.googleapis.com') || companyLogo.includes('firebasestorage.app');
      const timeout = isFirebaseStorageURL ? 5000 : 2000;
      logoDataUrl = await Promise.race([
        loadLogoAsDataUrl(companyLogo).catch(() => null),
        new Promise((r) => setTimeout(() => r(null), timeout)),
      ]);
    }
    if (!logoDataUrl) {
      logoDataUrl = await Promise.race([
        loadLogoAsDataUrl('/bba-logo.png').catch(() => null),
        new Promise((r) => setTimeout(() => r(null), 2000)),
      ]);
    }
    return { logoDataUrl, companyName, primaryColor, secondaryColor };
  };

  const buildKpiContentStyles = (primaryColor) => `<style>
    .kpi-doc { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .kpi-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; page-break-inside: avoid; break-inside: avoid; }
    .kpi-card h3 { font-size: 16px; font-weight: bold; margin: 0 0 8px; color: #111; text-align: center; }
    .kpi-value { font-size: 28px; font-weight: bold; color: ${primaryColor}; text-align: center; word-wrap: break-word; }
    .kpi-period { font-size: 12px; color: #6b7280; text-align: center; }
    .kpi-formula { font-size: 11px; color: #6b7280; text-align: center; margin-top: 6px; }
  </style>`;

  const kpiHtmlEscape = (text) =>
    String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const buildKpiBodyHtml = (job) => {
    const cards = (job.kpis || [])
      .map((kpi) => {
        const value = kpi.formula ? calculateKpiValue(kpi) : kpi.value;
        return `<div class="kpi-card">
          <h3>${kpiHtmlEscape(kpi.title || '')}</h3>
          <div class="kpi-value">${kpiHtmlEscape(value)}</div>
          <div class="kpi-period">${kpiHtmlEscape(kpi.period || '')}</div>
          ${kpi.formula ? `<div class="kpi-formula">Formula: ${kpiHtmlEscape(kpi.formula)}</div>` : ''}
        </div>`;
      })
      .join('');
    return `<div class="kpi-doc">${cards}</div>`;
  };

  // Render a role's KPIs through the master HTML template (CRM ▸ KPI Templates ▸
  // HTML Template tab), falling back to the starter layout when none is saved.
  const buildKpiDownloadHtml = async (job) => {
    const [branding, master] = await Promise.all([getKpiBranding(), fetchKpiHtmlTemplate()]);
    const { logoDataUrl, companyName, primaryColor, secondaryColor } = branding;
    const templateHtml = (master.html || '').trim() || DEFAULT_KPI_HTML_TEMPLATE;
    return (
      buildKpiContentStyles(primaryColor) +
      renderKpiHtmlTemplate(templateHtml, {
        logoUrl: logoDataUrl || '',
        title: job.title || '',
        department: job.department || '',
        body: buildKpiBodyHtml(job),
        companyName,
        primaryColor,
        secondaryColor,
      })
    );
  };

  const handleDownloadPDF = async () => {
    if (!selectedJob) return;
    try {
      const html = await buildKpiDownloadHtml(selectedJob);
      const fileName = `${String(selectedJob.title || 'kpi').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_kpis`;
      await downloadTailored('pdf', { html, fileName });
    } catch (error) {
      console.error('Error generating KPI PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleDownloadDoc = async () => {
    if (!selectedJob) return;
    try {
      const html = await buildKpiDownloadHtml(selectedJob);
      const fileName = `${String(selectedJob.title || 'kpi').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_kpis`;
      await downloadTailored('docx', { html, fileName });
    } catch (error) {
      console.error('Error generating KPI document:', error);
      alert('Error generating document. Please try again.');
    }
  };

  // Get display data
  const displayCompanyKPIs = companyGoals.map(goal => getDisplayKPI(goal, kpiTemplates[0]?.id));
  const displayDepartmentKPIs = getDisplayDepartmentKPIs();

  const escapeCsvCell = (value) => {
    const s = value == null ? '' : String(value);
    if (/[",\r\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const formatCompanyTrackingTarget = (value) => {
    if (value == null || value === '' || value === 'Set Value') return '—';
    return String(value);
  };

  const findCompanyKpiValue = (companyKpis, sourceLabels) => {
    for (const label of sourceLabels) {
      const k = companyKpis.find((g) => g.label === label);
      if (k) return k.value;
    }
    return null;
  };

  const buildCompanyTrackingRows = (companyKpis) =>
    COMPANY_TRACKING_ROW_DEFS.map((def) => {
      const raw = findCompanyKpiValue(companyKpis || [], def.sourceLabels);
      return {
        key: def.key,
        kind: 'company',
        companyRowLabel: `${def.rowTitle} (${formatCompanyTrackingTarget(raw)})`,
        cells: COMPANY_TRACKING_HEADERS.map(() => '')
      };
    });

  const buildTrackingRowsForTab = (tabId, companyKpis, deptKpis) => {
    if (tabId === 'company') {
      return buildCompanyTrackingRows(companyKpis);
    }
    const jobs = deptKpis[tabId] || [];
    const rows = [];
    jobs.forEach((job) => {
      (job.kpis || []).forEach((kpi) => {
        const valueStr =
          kpi.formula != null && kpi.formula !== '' ? calculateKpiValue(kpi) : kpi.value;
        const descParts = [
          kpi.title,
          kpi.period,
          valueStr != null && String(valueStr) !== '' ? `Target: ${valueStr}` : null
        ].filter(Boolean);
        rows.push({
          kind: 'department',
          who: job.title || '—',
          description: descParts.join(' — ') || '—',
          cells: TRACKING_HEADERS.slice(3).map(() => '')
        });
      });
    });
    return rows;
  };

  const emptyTrackingPlaceholder = '—';

  const buildCsvLinesForTrackingTab = (tabId) => {
    const rows = buildTrackingRowsForTab(tabId, displayCompanyKPIs, displayDepartmentKPIs);
    const sheetTitle = trackingSheetTabs.find((t) => t.id === tabId)?.label ?? tabId;
    const sectionHeader = `=== ${sheetTitle} ===`;
    if (tabId === 'company') {
      return [
        sectionHeader,
        ['', ...COMPANY_TRACKING_HEADERS].map(escapeCsvCell).join(','),
        ...rows.map((r) =>
          [
            r.companyRowLabel,
            ...r.cells.map((c) => c || emptyTrackingPlaceholder)
          ].map(escapeCsvCell).join(',')
        ),
        ''
      ];
    }
    return [
      sectionHeader,
      TRACKING_HEADERS.map(escapeCsvCell).join(','),
      ...rows.map((r) =>
        [
          r.who,
          r.description,
          emptyTrackingPlaceholder,
          ...r.cells.map((c) => c || emptyTrackingPlaceholder)
        ].map(escapeCsvCell).join(',')
      ),
      ''
    ];
  };

  const downloadTrackingCsv = () => {
    const dateStr = new Date().toISOString().slice(0, 10);
    const lines = [
      `KPI Tracking workbook,${dateStr}`,
      '',
      ...trackingSheetTabs.flatMap((tab) => buildCsvLinesForTrackingTab(tab.id))
    ];
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kpi-tracking-workbook-${dateStr}.csv`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  const escapeXmlData = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const xmlStringCell = (value) =>
    `<Cell><Data ss:Type="String">${escapeXmlData(value)}</Data></Cell>`;

  const buildCompanyTrackingTableXml = (rows) => {
    const headerRow = `<Row>${['', ...COMPANY_TRACKING_HEADERS].map(xmlStringCell).join('')}</Row>`;
    const body = rows
      .map(
        (r) =>
          `<Row>${[r.companyRowLabel, ...r.cells.map((c) => c || emptyTrackingPlaceholder)]
            .map(xmlStringCell)
            .join('')}</Row>`
      )
      .join('');
    return `<Table>${headerRow}${body}</Table>`;
  };

  const buildDepartmentTrackingTableXml = (rows) => {
    const headerRow = `<Row>${TRACKING_HEADERS.map((h) => xmlStringCell(h)).join('')}</Row>`;
    const body = rows
      .map(
        (r) =>
          `<Row>${[
            r.who,
            r.description,
            emptyTrackingPlaceholder,
            ...r.cells.map((c) => c || emptyTrackingPlaceholder)
          ]
            .map(xmlStringCell)
            .join('')}</Row>`
      )
      .join('');
    return `<Table>${headerRow}${body}</Table>`;
  };

  const worksheetXml = (displayName, tableInnerXml) => {
    const safe = String(displayName)
      .replace(/[:\\/?*[\]]/g, '')
      .trim()
      .slice(0, 31);
    const name = safe || 'Sheet';
    return `<Worksheet ss:Name="${escapeXmlData(name)}">${tableInnerXml}</Worksheet>`;
  };

  const downloadTrackingXls = () => {
    const dateStr = new Date().toISOString().slice(0, 10);
    const worksheets = trackingSheetTabs.map((tab) => {
      const rows = buildTrackingRowsForTab(tab.id, displayCompanyKPIs, displayDepartmentKPIs);
      const table =
        tab.id === 'company'
          ? buildCompanyTrackingTableXml(rows)
          : buildDepartmentTrackingTableXml(rows);
      return worksheetXml(tab.label, table);
    });
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
${worksheets.join('\n')}
</Workbook>`;

    const blob = new Blob(['\ufeff' + xml], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kpi-tracking-workbook-${dateStr}.xls`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  if (isLoading) {
    return (
      <div className={embedded ? '' : `relative min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-8'}`}>
        {!embedded && <PortalBackground />}
        <div className={embedded ? 'relative w-full' : `relative ${isMobile ? 'w-full' : 'max-w-7xl'} mx-auto`}>
          <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-md dark:shadow-gray-800 ${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center justify-center">
              <div className="text-gray-500 dark:text-gray-400">Loading KPIs...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Department KPIs: search over job title / KPI titles + values, then narrow to
  // the selected group. Mirrors the portal SOPs / Job Descriptions layout.
  const deptQuery = searchQuery.trim().toLowerCase();
  const jobMatchesSearch = (job) => {
    if (!deptQuery) return true;
    const inTitle = (job.title || '').toLowerCase().includes(deptQuery);
    const inKpis = (job.kpis || []).some(
      (kpi) =>
        (kpi.title || '').toLowerCase().includes(deptQuery) ||
        String(kpi.value ?? '').toLowerCase().includes(deptQuery)
    );
    return inTitle || inKpis;
  };
  const visibleDeptSections = KPI_SECTIONS
    .filter((s) => groupFilter === 'all' || s.id === groupFilter)
    .map((s) => ({ ...s, items: (displayDepartmentKPIs[s.id] || []).filter(jobMatchesSearch) }))
    .filter((s) => s.items.length > 0);

  // Company is its own filterable group (🎯), sharing the section header format.
  const COMPANY_SECTION = { id: 'company', label: 'Company', emoji: '🎯' };
  const companyMatchesSearch = (kpi) => {
    if (!deptQuery) return true;
    return (
      (kpi.label || '').toLowerCase().includes(deptQuery) ||
      String(kpi.value ?? '').toLowerCase().includes(deptQuery)
    );
  };
  const visibleCompanyKPIs = displayCompanyKPIs.filter(companyMatchesSearch);
  const showCompanySection =
    (groupFilter === 'all' || groupFilter === 'company') && visibleCompanyKPIs.length > 0;
  const nothingMatches = !showCompanySection && visibleDeptSections.length === 0;

  // KPI Tracking: same search + group filter, rendered as stacked tables
  // (Company section has its own header/columns) instead of spreadsheet tabs.
  const trackingSectionMeta = [
    { id: 'company', label: 'Company', emoji: '🎯' },
    { id: 'design', label: 'Design', emoji: '📐' },
    { id: 'construction', label: 'Construction', emoji: '👷' },
    { id: 'office', label: 'Office', emoji: '🏢' },
  ];
  const trackingQuery = trackingSearch.trim().toLowerCase();
  const trackingRowMatches = (row) => {
    if (!trackingQuery) return true;
    if (row.kind === 'company') return (row.companyRowLabel || '').toLowerCase().includes(trackingQuery);
    return (
      (row.who || '').toLowerCase().includes(trackingQuery) ||
      (row.description || '').toLowerCase().includes(trackingQuery)
    );
  };
  const visibleTrackingSections = trackingSectionMeta
    .filter((s) => trackingGroupFilter === 'all' || s.id === trackingGroupFilter)
    .map((s) => ({
      ...s,
      rows: buildTrackingRowsForTab(s.id, displayCompanyKPIs, displayDepartmentKPIs).filter(trackingRowMatches),
    }))
    .filter((s) => s.rows.length > 0);

  const renderSectionHeader = (label, emoji) => (
    <div className="flex items-center justify-center gap-3 mb-5">
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
        <span className="text-xl leading-none">{emoji}</span>
      </span>
      <h3 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {label}
      </h3>
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
    </div>
  );

  // Card styling mirrors the portal Job Descriptions cards.
  const cardShell = `rounded-xl border p-4 transition-shadow hover:shadow-md cursor-pointer ${
    darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
  }`;
  const viewBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700/60' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;
  const tailorBtn =
    'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors';
  // Once tailored, the Tailor button flips green (state reads from the action).
  const tailoredBtn =
    'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors';
  const versionPill = `shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
    darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
  }`;

  const renderRoleCard = (job, key) => {
    const version = String(job.version || '').trim();
    return (
      <div key={key} className={cardShell} onClick={() => handleJobView(job)}>
        <div className="flex items-center gap-3 mb-4">
          <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
            {job.emoji ? <span className="text-2xl leading-none">{job.emoji}</span> : <FileText className="w-5 h-5" />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {job.title}
              </h4>
              {version && <span className={versionPill}>v{version}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={(e) => { e.stopPropagation(); handleJobView(job); }} className={viewBtn}>
            <Eye className="w-3.5 h-3.5" />
            View
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleJobTailor(job); }}
            className={job.hasCustomizations ? tailoredBtn : tailorBtn}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {job.hasCustomizations ? 'Tailored' : 'Tailor'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? '' : `relative min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-8'}`}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? 'relative w-full' : 'relative max-w-7xl mx-auto'}>
        <div className={embedded ? 'rounded-lg' : `rounded-lg ${isMobile ? 'p-4' : 'p-6'}`}>
          {!embedded && (
            <div className="flex items-center mb-8">
              <BackButton onClick={() => window.history.back()} />
            </div>
          )}

          <div className="p-6">
          {/* Search + group filter + add new role — controls sit above every section */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search KPIs..."
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className={`sm:w-56 px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
              }`}
            >
              <option value="all">All groups</option>
              <option value="company">{COMPANY_SECTION.emoji} {COMPANY_SECTION.label}</option>
              {KPI_SECTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {(groupEmojis[s.id] || s.emoji)} {s.label}
                </option>
              ))}
            </select>
            <AddButton onClick={handleAddNew} className="shrink-0" title="Add new role" />
          </div>

          {nothingMatches && (
            <p className={`text-sm text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No KPIs match your search.
            </p>
          )}

          {/* Company KPIs */}
          {showCompanySection && (
            <div className="mb-12">
              {renderSectionHeader(COMPANY_SECTION.label, COMPANY_SECTION.emoji)}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {visibleCompanyKPIs.map((kpi, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 text-center cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                    onClick={() => handleKpiClick(kpi)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{kpi.label}</h3>
                    <div className="flex flex-col items-center">
                      <span className={`text-5xl font-bold mb-2 ${
                        kpi.value === 'Set Value'
                          ? 'text-gray-400 dark:text-gray-500 italic'
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {kpi.value}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{kpi.period}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Department KPIs — grouped sections, JD-style cards */}
          {visibleDeptSections.map((section) => (
            <div key={section.id} className="mb-12">
              {renderSectionHeader(section.label, groupEmojis[section.id] || section.emoji)}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((job, index) => renderRoleCard(job, `${section.id}-${index}`))}
              </div>
            </div>
          ))}

          {/* KPI Tracking (spreadsheet-style, data from company + role KPIs) */}
          <div className="mt-14 pt-10 border-t border-gray-200 dark:border-gray-700">
            <div className="relative mb-6 min-h-[3rem]">
              <div className="flex items-center justify-center gap-3 px-4 sm:px-40">
                <FaTable className="text-2xl text-red-600 dark:text-red-400 shrink-0" aria-hidden />
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white text-center">
                  KPI Tracking
                </h2>
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-2 sm:mt-0 sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 sm:justify-end">
                <button
                  type="button"
                  onClick={downloadTrackingCsv}
                  title="Exports Company, Design, Construction, and Office sections in one file"
                  className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <FaDownload className="mr-2 shrink-0" aria-hidden />
                  Download CSV
                </button>
                <button
                  type="button"
                  onClick={downloadTrackingXls}
                  title="Excel workbook with four worksheets: Company, Design, Construction, Office"
                  className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border border-transparent bg-green-700 text-white hover:bg-green-800 dark:bg-green-600 dark:hover:bg-green-500"
                >
                  <FaDownload className="mr-2 shrink-0" aria-hidden />
                  Download XLS
                </button>
              </div>
            </div>

            {/* Tracking search + group filter (matches the Department layout) */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search
                  className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                    darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}
                />
                <input
                  type="text"
                  value={trackingSearch}
                  onChange={(e) => setTrackingSearch(e.target.value)}
                  placeholder="Search tracking rows..."
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
              <select
                value={trackingGroupFilter}
                onChange={(e) => setTrackingGroupFilter(e.target.value)}
                className={`sm:w-56 px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
                }`}
              >
                <option value="all">All groups</option>
                {trackingSectionMeta.map((s) => (
                  <option key={s.id} value={s.id}>
                    {(s.id === 'company' ? s.emoji : groupEmojis[s.id] || s.emoji)} {s.label}
                  </option>
                ))}
              </select>
            </div>

            {visibleTrackingSections.length === 0 && (
              <p className={`text-sm text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No tracking rows match your search.
              </p>
            )}

            {visibleTrackingSections.map((section) => (
              <div key={section.id} className="mt-10 first:mt-0">
                {renderSectionHeader(
                  section.label,
                  section.id === 'company' ? section.emoji : groupEmojis[section.id] || section.emoji
                )}
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                  <table className="min-w-max w-full text-left text-xs border-collapse font-mono bg-white dark:bg-gray-900">
                    <thead>
                      {section.id === 'company' ? (
                        <tr>
                          <th
                            aria-hidden
                            className="sticky top-0 z-10 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-2.5 w-12 min-w-[10rem]"
                          />
                          {COMPANY_TRACKING_HEADERS.map((header) => (
                            <th
                              key={header}
                              className="sticky top-0 z-10 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-2.5 font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap text-center"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      ) : (
                        <tr>
                          {TRACKING_HEADERS.map((header) => (
                            <th
                              key={header}
                              className="sticky top-0 z-10 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-2.5 font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {section.id === 'company'
                        ? section.rows.map((row) => (
                            <tr
                              key={row.key}
                              className="odd:bg-white even:bg-gray-50/80 dark:odd:bg-gray-900 dark:even:bg-gray-800/60"
                            >
                              <td className="border border-gray-200 dark:border-gray-700 px-2 py-2 font-medium text-gray-900 dark:text-gray-100 align-top">
                                {row.companyRowLabel}
                              </td>
                              {row.cells.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className="border border-gray-200 dark:border-gray-700 px-1 py-2 text-center text-gray-400 dark:text-gray-500 min-w-[3rem] align-top"
                                >
                                  {cell || emptyTrackingPlaceholder}
                                </td>
                              ))}
                            </tr>
                          ))
                        : section.rows.map((row, rowIdx) => (
                            <tr
                              key={`${row.who}-${rowIdx}-${row.description?.slice(0, 24) || ''}`}
                              className="odd:bg-white even:bg-gray-50/80 dark:odd:bg-gray-900 dark:even:bg-gray-800/60"
                            >
                              <td className="border border-gray-200 dark:border-gray-700 px-2 py-2 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap align-top">
                                {row.who}
                              </td>
                              <td className="border border-gray-200 dark:border-gray-700 px-2 py-2 text-gray-800 dark:text-gray-200 max-w-md align-top">
                                {row.description}
                              </td>
                              <td className="border border-gray-200 dark:border-gray-700 px-2 py-2 text-center text-gray-400 dark:text-gray-500 align-top">
                                {emptyTrackingPlaceholder}
                              </td>
                              {row.cells.map((cell, ci) => (
                                <td
                                  key={ci}
                                  className="border border-gray-200 dark:border-gray-700 px-1 py-2 text-center text-gray-400 dark:text-gray-500 min-w-[2.75rem] align-top"
                                >
                                  {cell || emptyTrackingPlaceholder}
                                </td>
                              ))}
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && selectedKPI && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full p-6">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 text-center">Edit {selectedKPI.label}</h2>
            
            <div className="flex flex-col items-center mb-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleDecrement}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <FaArrowDown className="text-xl text-gray-600 dark:text-gray-300" />
                </button>
                
                <input
                  type="text"
                  value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                  className="text-5xl font-bold text-gray-900 dark:text-white text-center w-48 bg-transparent border-none focus:outline-none"
                />
                
                <button
                  onClick={handleIncrement}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <FaArrowUp className="text-xl text-gray-600 dark:text-gray-300" />
                </button>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-2">{selectedKPI.period}</span>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 dark:hover:bg-red-500"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Details Modal — mirrors the Job Descriptions tailor modal chrome */}
      {isJobModalOpen && (selectedJob || editedJob) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col relative`}>
            <button
              type="button"
              onClick={handleJobModalClose}
              title="Close"
              aria-label="Close"
              className={`absolute top-3 left-3 z-10 p-1.5 rounded-full transition ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 overflow-y-auto thin-scrollbar px-4 sm:px-6 md:px-8 pt-12 pb-4">
              <div className="flex flex-col items-center mb-8">
                {isEditing ? (
                  <>
                    <select
                      value={editedJob.department}
                      onChange={handleDepartmentChange}
                      className="mb-4 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    >
                      <option value="design">Design</option>
                      <option value="construction">Construction</option>
                      <option value="office">Office</option>
                    </select>
                    <input
                      type="text"
                      value={editedJob.title}
                      onChange={handleJobTitleChange}
                      className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-red-500 dark:focus:border-red-400 focus:outline-none"
                    />
                  </>
                ) : (
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{selectedJob.title}</h2>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {(isEditing ? editedJob : selectedJob).kpis.map((kpi, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 relative"
                  >
                    {isEditing && (
                      <div className="absolute top-2 right-2 flex gap-2">
                        <button
                          onClick={() => handleFormulaClick(kpi, index)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                          title="Set Formula"
                        >
                          <FaCalculator />
                        </button>
                        <button
                          onClick={() => handleDeleteKpi(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={kpi.title}
                          onChange={(e) => handleKpiChange(index, 'title', e.target.value)}
                          className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center w-full bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-red-500 dark:focus:border-red-400 focus:outline-none"
                        />
                        <div className="flex flex-col items-center">
                          <div className="relative w-full">
                            <input
                              type="text"
                              value={kpi.formula ? calculateKpiValue(kpi) : kpi.value}
                              onChange={(e) => handleKpiChange(index, 'value', e.target.value)}
                              className={`text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-2 text-center w-full bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-red-500 dark:focus:border-red-400 focus:outline-none ${kpi.formula ? 'text-green-600 dark:text-green-400' : ''}`}
                              readOnly={!!kpi.formula}
                            />
                            {kpi.formula && (
                              <div className="text-xs text-center text-gray-500 dark:text-gray-400">
                                Formula: {kpi.formula}
                              </div>
                            )}
                          </div>
                          <input
                            type="text"
                            value={kpi.period}
                            onChange={(e) => handleKpiChange(index, 'period', e.target.value)}
                            className="text-sm text-gray-500 dark:text-gray-400 text-center w-full bg-transparent border-b-2 border-gray-300 dark:border-gray-600 focus:border-red-500 dark:focus:border-red-400 focus:outline-none"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">{kpi.title}</h3>
                        <div className="flex flex-col items-center">
                          <span className={`text-4xl font-bold mb-2 ${
                            kpi.value === 'Set Company KPIs First' 
                              ? 'text-orange-600 dark:text-orange-400' 
                              : kpi.formula 
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-gray-900 dark:text-white'
                          }`}>
                            {kpi.formula ? calculateKpiValue(kpi) : kpi.value}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{kpi.period}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {isEditing && (
                <div className="flex justify-center mb-2">
                  <button
                    onClick={handleAddKpi}
                    className="flex items-center text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    <FaPlus className="mr-2" />
                    Add New KPI
                  </button>
                </div>
              )}
            </div>

            {/* Sticky footer — matches the Job Descriptions tailor modal */}
            <div className={`shrink-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-4 sm:px-6 md:px-8 py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                {!isEditing && selectedJob?.hasCustomizations && (
                  <button
                    onClick={handleResetJobToTemplate}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Reset to Template
                  </button>
                )}
                {!isEditing && <EditButton label="Tailor" onClick={handleEdit} />}
              </div>
              <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleJobCancel}
                      className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                    >
                      Cancel
                    </button>
                    <SaveButton onClick={handleJobSave} variant="green" />
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleDownloadDoc}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Download DOC</span>
                      <span className="sm:hidden">DOC</span>
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Download PDF</span>
                      <span className="sm:hidden">PDF</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Modal */}
      {showFormulaModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-4xl w-full p-6">
            <div className="flex justify-center items-center mb-6 relative">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                Build KPI Formula
              </h2>
              <button
                onClick={handleFormulaModalClose}
                className="absolute right-0 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Formula Builder */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Build Your Formula
                  </label>
                  
                  {/* Formula Display */}
                  <div className="p-4 mb-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <div className="text-sm font-medium mb-2">Current Formula:</div>
                    <div className="font-mono text-lg text-gray-900 dark:text-white">
                      {formulaBuilder.length > 0 ? buildFormulaString() : 'No formula built yet'}
                    </div>
                  </div>

                  {/* Formula Builder Controls */}
                  <div className="space-y-3">
                    {/* Variables */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Add Variable
                      </label>
                      <select
                        className="w-full px-3 py-2 border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        onChange={(e) => {
                          if (e.target.value) {
                            const [category, key] = e.target.value.split('.');
                            handleAddToFormula({ type: 'variable', category, key, value: `{${category}.${key}}` });
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Select a variable...</option>
                        {formulaParser && formulaParser.getAvailableVariables().map((variable, idx) => (
                          <option key={idx} value={`${variable.key}`}>
                            {variable.display} ({variable.value})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Operations */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Add Operation
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {availableOperations.map((op) => (
                          <button
                            key={op.value}
                            onClick={() => handleAddToFormula({ type: 'operation', value: op.value })}
                            className="px-3 py-2 border rounded-md text-sm font-medium border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            {op.symbol}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Number Input */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                        Add Number
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={numberInput}
                          onChange={(e) => setNumberInput(e.target.value)}
                          placeholder="Enter a number"
                          className="flex-1 px-3 py-2 border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddNumberToFormula();
                            }
                          }}
                        />
                        <button
                          onClick={handleAddNumberToFormula}
                          className="px-4 py-2 border rounded-md text-sm font-medium border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Clear Formula */}
                    <button
                      onClick={handleClearFormula}
                      className="w-full px-3 py-2 border rounded-md text-sm font-medium border-red-500 bg-red-500 text-white hover:bg-red-600"
                    >
                      Clear Formula
                    </button>
                  </div>
                </div>
              </div>

              {/* Formula Preview */}
              <div className="space-y-4">
                {/* Available Variables */}
                {formulaParser && (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Available Variables
                    </label>
                    <div className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        {formulaParser.getAvailableVariables().map((variable, idx) => (
                          <div key={idx} className="p-2 rounded bg-gray-100 dark:bg-gray-600">
                            <span className="font-mono text-blue-600 dark:text-blue-400">{variable.display}</span>
                            <span className="ml-2 text-gray-500 dark:text-gray-400">→ {variable.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {formulaError && (
                  <div className="text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-lg">
                    {formulaError}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleFormulaModalClose}
                className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleFormulaSave}
                className="px-4 py-2 bg-red-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-red-700"
              >
                Save Formula
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalKPIs; 